import { nextTick, onScopeDispose, watch, type Ref } from 'vue';

export interface UseFocusTrapOptions {
    /** Reactive flag controlling whether the trap is active. */
    active: Ref<boolean>;
    /** Invoked when Escape is pressed while the trap is active. */
    onEscape?: () => void;
    /** Restore focus to the previously focused element when the trap deactivates. Default: true. */
    restoreFocus?: boolean;
}

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
].join(',');

/**
 * Minimal, dependency-free focus trap for modal overlays (dialogs, drawers).
 *
 * While `active` is true it:
 *  - moves focus into `target` (first focusable element, or the container itself),
 *  - keeps Tab / Shift+Tab cycling within `target`,
 *  - calls `onEscape` when Escape is pressed,
 *  - restores focus to the previously focused element on deactivate (and on unmount).
 *
 * The `target` element should be focusable as a fallback (give it `tabindex="-1"`).
 */
export function useFocusTrap(target: Ref<HTMLElement | null>, options: UseFocusTrapOptions): void {
    const { active, onEscape, restoreFocus = true } = options;

    let previouslyFocused: HTMLElement | null = null;
    let trapped = false;

    const focusable = (): HTMLElement[] => {
        const root = target.value;
        if (!root) return [];
        const all = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
        // Skip elements hidden via display:none / visibility:hidden (e.g. nav items
        // only shown at certain breakpoints, like the sidebar logo on desktop).
        const visible = all.filter((el) => typeof el.checkVisibility !== 'function' || el.checkVisibility());
        // In layout-less environments (jsdom) checkVisibility may report everything
        // hidden; fall back to the full list so focus management still works.
        return visible.length > 0 ? visible : all;
    };

    const onKeydown = (event: KeyboardEvent): void => {
        if (!trapped) return;

        if (event.key === 'Escape') {
            event.stopPropagation();
            onEscape?.();
            return;
        }

        if (event.key !== 'Tab') return;

        const root = target.value;
        if (!root) return;

        const items = focusable();

        // Nothing focusable inside: keep focus pinned to the container.
        if (items.length === 0) {
            event.preventDefault();
            root.focus();
            return;
        }

        const first = items[0];
        const last = items[items.length - 1];
        const current = document.activeElement as HTMLElement | null;

        if (event.shiftKey) {
            if (current === first || !root.contains(current)) {
                event.preventDefault();
                last.focus();
            }
        } else if (current === last || !root.contains(current)) {
            event.preventDefault();
            first.focus();
        }
    };

    const activate = async (): Promise<void> => {
        if (trapped) return;
        trapped = true;
        previouslyFocused = (document.activeElement as HTMLElement) ?? null;
        document.addEventListener('keydown', onKeydown, true);
        await nextTick();
        const items = focusable();
        (items[0] ?? target.value)?.focus();
    };

    const deactivate = (): void => {
        if (!trapped) return;
        trapped = false;
        document.removeEventListener('keydown', onKeydown, true);
        if (restoreFocus && previouslyFocused?.isConnected) {
            previouslyFocused.focus();
        }
        previouslyFocused = null;
    };

    watch(active, (isActive) => (isActive ? void activate() : deactivate()), { immediate: true });

    onScopeDispose(deactivate);
}
